import { ServiceRequestDetail } from "@features/cx/services/types";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import {
  AlertCircle,
  AlertTriangle,
  Building,
  Calendar,
  CalendarIcon,
  CheckCircle,
  CheckSquare,
  Clock,
  Cog,
  DollarSign,
  FileText,
  Info,
  MapPinIcon,
  Settings,
  Wrench,
  X,
  Zap,
} from "lucide-react";
import React, { useEffect } from "react";

interface ClosureRequirements {
  customerNotification: boolean;
  serviceCompletion: boolean;
  qualityCheck: boolean;
  documentationUpload: boolean;
}

interface ServiceDetailsTabProps {
  request: ServiceRequestDetail;
  onResolve?: (closureRequirements: any) => void;
  onReject?: (requestId: string) => void;
  closureRequirements?: any;
  setClosureRequirements?: any;
}

const ServiceDetailsTab: React.FC<ServiceDetailsTabProps> = ({
  request,
  onResolve,
  onReject,
  closureRequirements,
  setClosureRequirements,
}) => {
  // const [closureRequirements, setClosureRequirements] =
  //   useState<ClosureRequirements>({
  //     customerNotification: false,
  //     serviceCompletion: false,
  //     qualityCheck: false,
  //     documentationUpload: false,
  //   });

  const closureRequirementsData =
    request?.additionalData?.closureRequirements || {};

  // Update state when request data loads
  useEffect(() => {
    const closure = request?.additionalData?.closureRequirements;

    if (closure) {
      setClosureRequirements({
        customerNotification: closure.customerNotification ?? false,
        serviceCompletion: closure.serviceCompletionVerification ?? false,
        qualityCheck: closure.qualityCheckPending ?? false,
        documentationUpload: closure.documentationUpload ?? false,
      });
    }
  }, [request?.additionalData]);

  const currentStatus = String(request?.statusDisplay || "");

  const getStatusIcon = (status: string) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "completed":
        return <CheckCircle className="h-3 w-3 text-green-500" />;
      case "approved":
        return <CheckCircle className="h-3 w-3 text-green-500" />;
      case "pending":
        return <Clock className="h-3 w-3 text-yellow-500" />;
      case "in_progress":
        return <Settings className="h-3 w-3 text-blue-500" />;
      case "rejected":
        return <X className="h-3 w-3 text-red-500" />;
      case "cancelled":
        return <X className="h-3 w-3 text-gray-500" />;
      case "on_hold":
        return <AlertTriangle className="h-3 w-3 text-orange-500" />;
      default:
        return <AlertCircle className="h-3 w-3 text-gray-500" />;
    }
  };

  const formatStatus = (status: string) => {
    if (!status) return "Unknown";
    return status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ");
  };

  const handleClosureRequirementChange = (
    requirement: keyof ClosureRequirements,
    checked: boolean
  ) => {
    setClosureRequirements((prev) => ({
      ...prev,
      [requirement]: checked,
    }));
    console.log('debug state update', requirement, checked)
  };

  const handleResolveRequest = async () => {
    // Convert closureRequirements to snake_case
    const closureRequirementsSnakeCase = {
      customer_notification: closureRequirements.customerNotification,
      service_completion_verification: closureRequirements.serviceCompletion,
      quality_check_pending: closureRequirements.qualityCheck,
      documentation_upload: closureRequirements.documentationUpload,
    };
    console.log('debug resolve clicked', closureRequirements)
    if (onResolve) {
      onResolve(closureRequirements);
    }
  };

  const handleRejectRequest = async () => {
    if (onReject) {
      onReject(request?.id.toString());
    }
    await queryClient.invalidateQueries({
      queryKey: QueryKeyFactory.module.cx.serviceRequests.details(
        String(request?.id)
      ),
    });
  };

  console.log("reqquest", request);

  return (
    <Card className="border-none p-0 m-0 shadow-none">
      <CardContent>
        <div className="space-y-6">
          <div className="space-y-4 p-4 border border-gray-200 rounded-md">
            <h4 className="card-heading-text flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Basic Information
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="card-subheading-text">Service Type</p>
                <p className="text-sm">
                  {request?.utilitySupportRequest.name ?? "NA"}
                </p>
              </div>
              <div>
                <p className="card-subheading-text">Service Code</p>
                <p className="text-sm">{request?.requestNo}</p>
              </div>
              <div>
                <p className="card-subheading-text">Category</p>
                <p className="text-sm">
                  {request?.utilitySupportRequest.supportRequestType ?? "NA"}
                </p>
              </div>
              <div>
                <p className="card-subheading-text">Sub Category</p>
                <p className="text-sm">
                  {request?.utilitySupportRequest.supportRequestSubtype ?? "NA"}
                </p>
              </div>
              <div>
                <p className="card-subheading-text">Service Fee</p>
                <p className="text-sm">
                  ${" "}
                  {request?.utilitySupportRequest?.extraData?.serviceCharge ??
                    "NA"}
                </p>
              </div>

              <div>
                <p className="card-subheading-text">Preferred Date</p>
                <p className="text-sm">
                  {request?.additionalData.scheduleDate || "NA"}
                </p>
              </div>
              <div>
                <p className="card-subheading-text">Preferred Time Slot</p>
                <p className="text-sm">
                  {request?.additionalData.preferredTimeSlot ?? "NA"}
                </p>
              </div>
              <div>
                <p className="card-subheading-text">Field Work Required</p>
                <p className="text-sm">
                  {request?.utilitySupportRequest?.extraData?.fieldForceRequired
                    ? "Yes"
                    : "No"}
                </p>
              </div>
              <div>
                <p className="card-subheading-text">Date Created</p>
                <p className="text-sm">{request?.createdDate}</p>
              </div>
              <div>
                <p className="card-subheading-text">Utility Service</p>
                <p className="text-sm">
                  {request?.utilitySupportRequest?.productCode?.join(",") ??
                    "NA"}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md">
              {/* SLA Target */}
              <div className="border border-gray-200 p-4 rounded-md">
                <p className="card-heading-text mb-2">SLA Target</p>
                <div className="grid grid-cols-3 w-full gap-2 text-sm text-muted-foreground">
                  <div className="text-center">
                    <p className="mb-1 flex justify-center items-center gap-2">
                      Acknowledgement{" "}
                      <CustomTooltip message="Target time taken to acknowledge a request after it is raised." />{" "}
                    </p>
                    <p className="text-foreground font-medium">
                      {request?.utilitySupportRequest?.extraData
                        ?.acknowlegmentTime ?? "NA"}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="mb-1 flex justify-center items-center gap-2">
                      Response{" "}
                      <CustomTooltip message="Target time taken to give the first response to a request." />
                    </p>
                    <p className="text-foreground font-medium text-center">
                      {request?.utilitySupportRequest?.extraData
                        ?.responseTime ?? "NA"}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="mb-1 flex justify-center items-center gap-2">
                      Resolution{" "}
                      <CustomTooltip message="Target time taken to fully resolve and close the request." />{" "}
                    </p>
                    <p className="text-foreground font-medium">
                      {request?.utilitySupportRequest?.extraData
                        ?.resolutionTime ?? "NA"}
                    </p>
                  </div>
                </div>
              </div>

              {/* SLA Achieved */}
              <div className="border border-gray-200 p-4 rounded-md">
                <p className="card-heading-text mb-2">SLA Achieved</p>
                <div className="grid grid-cols-3 w-full text-sm text-muted-foreground">
                  <div className="text-center">
                    <p className="mb-1 flex justify-center items-center gap-2">
                      Acknowledgement{" "}
                      <CustomTooltip message="Actual time taken to acknowledge a request after it is raised." />{" "}
                    </p>

                    <p className="text-green-600 font-medium">
                      {request?.acknowledgementTimeAchieved ?? "Pending"}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="mb-1 flex justify-center items-center gap-2">
                      Response{" "}
                      <CustomTooltip message="Actual time taken to give the first response to a request." />
                    </p>

                    <p className="text-green-600 font-medium">
                      {request?.responseTime ?? "Pending"}
                    </p>
                  </div>

                  <div className="text-center">
                    <p className="mb-1 flex justify-center items-center gap-2">
                      Resolution{" "}
                      <CustomTooltip message="Actual time taken to fully resolve and close the request." />{" "}
                    </p>

                    <p className="text-green-600 font-medium">
                      {request?.resolutionTimeAchieved ?? "Pending"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-l-4 border-black-400 p-2 rounded mt-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-4 w-4" />
                </div>
                <div className="ml-2">
                  <p className="text-xs">
                    {request?.slaSummary.slaDueDate &&
                    new Date(request?.slaSummary.slaDueDate) > new Date()
                      ? `This request is due in ${Math.ceil(
                          (new Date(request?.slaSummary.slaDueDate).getTime() -
                            new Date().getTime()) /
                            (1000 * 60 * 60 * 24)
                        )} days.`
                      : "Please handle this request promptly."}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 p-4 border border-gray-200 rounded-md">
            <h4 className="card-heading-text flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Service Description
            </h4>
            <div>
              <p className="card-subheading-text text-muted-foreground">
                Description
              </p>
              <p className="text-sm bg-white p-2 rounded border mt-1">
                {request?.utilitySupportRequest.longDescription ||
                  "No description provided"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4  p-4 border border-gray-200 rounded-md">
              <h4 className="card-heading-text flex items-center gap-2">
                <MapPinIcon className="h-4 w-4" />
                Access Information
              </h4>
              <div>
                <p className="card-subheading-text text-muted-foreground">
                  Access Instructions
                </p>
                <p className="text-sm bg-white p-2 rounded border mt-1">
                  {request?.additionalData.additionalInstruction ?? "NA"}
                </p>
              </div>

              <div className="border-l-4 p-2 rounded mt-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Info className="h-4 w-4" />
                  </div>
                  <div className="ml-2">
                    <p className="text-xs">
                      Follow all access instructions carefully to avoid delays.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4 p-4 border border-gray-200 rounded-md">
              <h4 className="card-heading-text flex items-center gap-2">
                <CheckSquare className="h-4 w-4" />
                Closure Requirements
              </h4>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="customerNotification"
                    checked={closureRequirementsData?.customerNotification}
                    onCheckedChange={(checked) =>
                      handleClosureRequirementChange(
                        "customerNotification",
                        checked as boolean
                      )
                    }
                  />
                  <label
                    htmlFor="customerNotification"
                    className="text-sm cursor-pointer"
                  >
                    Customer notification
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="serviceCompletion"
                    checked={closureRequirementsData?.customerNotification}
                    onCheckedChange={(checked) =>
                      handleClosureRequirementChange(
                        "serviceCompletion",
                        checked as boolean
                      )
                    }
                  />
                  <label
                    htmlFor="serviceCompletion"
                    className="text-sm cursor-pointer"
                  >
                    Service completion verification
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="qualityCheck"
                    checked={closureRequirementsData?.qualityCheckPending}
                    onCheckedChange={(checked) =>
                      handleClosureRequirementChange(
                        "qualityCheck",
                        checked as boolean
                      )
                    }
                  />
                  <label
                    htmlFor="qualityCheck"
                    className="text-sm cursor-pointer"
                  >
                    Quality check pending
                  </label>
                </div>
              </div>

              <div className=" border-l-4 border-black-400 p-2 rounded mt-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Info className="h-4 w-4" />
                  </div>
                  <div className="ml-2">
                    <p className="text-xs">
                      Complete all requirements before closing the request.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceDetailsTab;
